/**
 * SWR 1.x aceptaba `initialData` para sembrar el cache con datos de SSR. Desde
 * SWR 2 la opción se llama `fallbackData` y las claves desconocidas se ignoran
 * en silencio: sin esta traducción los datos que entrega `getStaticProps` se
 * descartan y cada montaje vuelve a pedir todo desde el navegador.
 *
 * Se valida la forma del payload antes de sembrarlo porque hay llamadas que
 * pasan `null`, el string `'{}'` (fallback de `usePreloadedData`) o un objeto
 * con la data en `undefined` cuando el SSR falló. Sembrar esos casos haría que
 * SWR reporte "hay datos" y las páginas rendericen un 404 en vez del loader.
 */

/**
 * Config de SWR que además acepta el `initialData` legado de SWR 1.x, traducido
 * a `fallbackData` por `normalizeInitialData`.
 */
export type LegacySwrConfiguration<T> = T & { initialData?: unknown }

const hasGraphqlPayload = (value: unknown): boolean => {
    if (!value || typeof value !== 'object') return false

    const { data } = value as { data?: unknown }
    if (!data || typeof data !== 'object') return false

    return Object.values(data as Record<string, unknown>).some(
        (entry) => entry !== undefined && entry !== null
    )
}

/** `useSWRInfinite` espera un array de páginas; `useSWR`, una sola respuesta. */
const isUsablePayload = (value: unknown): boolean =>
    Array.isArray(value)
        ? value.length > 0 && value.every(hasGraphqlPayload)
        : hasGraphqlPayload(value)

export const normalizeInitialData = <T extends object>(
    swrOptions?: T
): T | undefined => {
    if (!swrOptions || !('initialData' in swrOptions)) return swrOptions

    const { initialData, ...rest } = swrOptions as T & { initialData?: unknown }

    return isUsablePayload(initialData)
        ? ({ ...rest, fallbackData: initialData } as T)
        : (rest as T)
}
